import React, { useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { useAuth } from "../../context/AuthContext";
import { API_BASE_URL } from "../../Helpers/apiConfig";
import { ensureStudentResponsibility } from "../../Helpers/studentResponsibility";
import { confirmGoogleRegisterConsent } from "../../Helpers/googleRegisterConsent";
import { traceUserLogin } from "../../Helpers/traceUserLogin";

const GOOGLE_CLIENT_ID = "828157744682-s3357qbm9vegagthio8a7dvi476j4e1c.apps.googleusercontent.com";
const ROLE_NAMES = { Student: "Öğrenci", Manager: "Yönetici", Teacher: "Öğretmen" };

// Backend mesajlarını kullanıcıya anlaşılır hale getirir.
const friendlyMessage = (message, fallback) => {
    if (!message) return fallback;
    if (message === "Kullanıcı Bulunamadı") {
        return "Bu Google hesabıyla kayıtlı bir üyelik bulunamadı. Önce \"Google ile Kaydolun\" butonuyla üye olabilirsiniz.";
    }
    if (message.startsWith("Google Login Hatası")) {
        return "Google doğrulaması şu an tamamlanamadı. Lütfen birkaç saniye bekleyip tekrar deneyin.";
    }
    return message;
};

const showLoading = (title) => Swal.fire({
    title,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => Swal.showLoading()
});

const showError = (text) => Swal.fire({ icon: "error", title: "Giriş yapılamadı", text, confirmButtonText: "Tamam" });

// Giriş ve Kayıt sayfalarında ortak "Google ile Kaydolun" / "Google ile Oturum Açın" butonları.
// İstek sürerken ikinci istek gönderilmez (art arda tıklamada API hatasını önler) ve
// kullanıcıya "Giriş yapılıyor..." gösterilir.
export default function GoogleAuthButtons() {
    const { setUser } = useAuth();
    const [registerConsented, setRegisterConsented] = useState(false);
    const busyRef = useRef(false);

    const loginWithCredential = async (credential) => {
        const res = await axios.post(API_BASE_URL + "/User/GoogleLogin/GoogleLogin", { idToken: credential });
        if (!res.data.success) {
            return res.data.message;
        }
        const userData = res.data.data;
        await traceUserLogin(userData);
        Swal.close();
        await ensureStudentResponsibility(userData);
        const roleName = userData.user?.roleName || userData.User?.roleName;
        setUser({
            name: userData.user?.fullName || userData.User?.fullName,
            user: userData.user || userData.User,
            token: userData.token || userData.Token,
            tokenData: userData.tokenExpiryDate || userData.TokenExpiryDate,
            role: ROLE_NAMES[roleName] || roleName,
            userDetail: userData.detail || userData.Detail,
            educationals: userData.educationals || userData.Educationals || [],
            educational: userData.educational || userData.Educational || null
        });
        window.location.href = "/";
        return null;
    };

    const handleGoogleLogin = async (credentialResponse) => {
        if (busyRef.current) return;
        busyRef.current = true;
        showLoading("Giriş yapılıyor...");
        try {
            const error = await loginWithCredential(credentialResponse.credential);
            if (error) {
                showError(friendlyMessage(error, "Giriş sırasında bir hata oluştu."));
                busyRef.current = false;
            }
        } catch (err) {
            showError(friendlyMessage(err.response?.data?.message, "Sunucuya ulaşılamadı. Lütfen tekrar deneyin."));
            busyRef.current = false;
        }
    };

    const handleGoogleRegister = async (credentialResponse) => {
        if (busyRef.current) return;
        busyRef.current = true;
        showLoading("Kaydınız oluşturuluyor...");
        try {
            const res = await axios.post(API_BASE_URL + "/User/GoogleRegister/GoogleRegister",
                { idToken: credentialResponse.credential });
            if (!res.data.success) {
                Swal.fire({ icon: "warning", title: "Kayıt yapılamadı", text: res.data.message, confirmButtonText: "Tamam" });
                busyRef.current = false;
                return;
            }
            // Kayıt başarılıysa aynı Google oturumuyla doğrudan giriş yapılır.
            showLoading("Kaydınız oluşturuldu, giriş yapılıyor...");
            const error = await loginWithCredential(credentialResponse.credential);
            if (error) {
                Swal.fire({
                    icon: "success",
                    title: "Kaydınız oluşturuldu",
                    text: "\"Google ile Oturum Açın\" butonuyla giriş yapabilirsiniz.",
                    confirmButtonText: "Tamam"
                });
                busyRef.current = false;
            }
        } catch (err) {
            showError(err.response?.data?.message || "Kayıt sırasında bir hata oluştu. Lütfen tekrar deneyin.");
            busyRef.current = false;
        }
    };

    const handleRegisterButtonClick = async () => {
        const accepted = await confirmGoogleRegisterConsent();
        if (accepted) setRegisterConsented(true);
    };

    const handleGoogleError = () => showError("Google bağlantısı kurulamadı. Lütfen tekrar deneyin.");

    return (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <div className="row justify-content-center">
                {/* Sol: Google ile Kaydolun (önce kayıt koşulları onaylanır) */}
                <div className="col-12 col-md-6">
                    <div className="d-flex flex-column align-items-center gap-2 mt-3">
                        <div className="mt-2">
                            {registerConsented ? (
                                <GoogleLogin
                                    onSuccess={handleGoogleRegister}
                                    onError={handleGoogleError}
                                    text="signup_with"
                                    shape="pill"
                                    size="large"
                                    width="250"
                                />
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-light border rounded-pill px-4 py-2"
                                    onClick={handleRegisterButtonClick}
                                >
                                    Google ile Kaydolun
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sağ: Google ile Oturum Açın */}
                <div className="col-12 col-md-6">
                    <div className="d-flex flex-column align-items-center gap-2 mt-3">
                        <div className="mt-2">
                            <GoogleLogin
                                onSuccess={handleGoogleLogin}
                                onError={handleGoogleError}
                                text="signin_with"
                                shape="pill"
                                size="large"
                                width="250"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </GoogleOAuthProvider>
    );
}
